import React from 'react';
import { motion } from 'framer-motion';
import { FiBriefcase, FiTarget, FiUsers, FiZap, FiShield, FiTrendingUp } from 'react-icons/fi';
import '../styles/Pages.css';
import EnhancedHeader from '../components/EnhancedHeader';

const Services = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  const services = [
    { icon: FiBriefcase, title: 'Behavioral Support', desc: 'Top notch behavior management and trauma informed care' },
    { icon: FiTarget, title: 'Life Skills Training', desc: 'Budgeting, cooking, independence and daily living skills' },
    { icon: FiUsers, title: 'Mental Health Services', desc: 'Professional therapist and mental health crisis interventions' },
    { icon: FiZap, title: 'Educational Support', desc: 'Training opportunities to foster employment pathways' },
    { icon: FiShield, title: 'Safeguarding', desc: 'Comprehensive safeguarding policies and procedures' },
    { icon: FiTrendingUp, title: 'Employment Support', desc: 'Assistance with opportunities and career development' }
  ];

  return (
    <div className="page-container">
      <EnhancedHeader
        title="Our Services"
        subtitle="Comprehensive Care Solutions"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(230, 126, 34, 0.7) 0%, rgba(231, 76, 60, 0.7) 100%)"
      />

      {/* Services Grid with Image 1 */}
      <motion.section
        className="services-grid-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="services-with-image">
            <div className="services-grid">
              {services.map((service, i) => (
                <motion.div
                  key={i}
                  className="service-card"
                  variants={itemVariants}
                  whileHover={{
                    y: -15,
                    boxShadow: '0 20px 40px rgba(52, 152, 219, 0.2)',
                    backgroundColor: 'rgba(52, 152, 219, 0.05)'
                  }}
                >
                  <service.icon className="service-icon" />
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="services-image">
              {/* IMAGE PLACEMENT 1: Services Photo */}
              <img src="/images/acr20.jpg" alt="Our Services" />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Specialized Support Section */}
      <motion.section
        className="specialized-support-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Specialized Support We Provide</h2>

          <div className="support-list">
            {[
              'Safeguarding and protection concerns',
              'Forensic and criminal history support',
              'Addiction and rehabilitation services',
              'Behavioral health management',
              'Educational and employment pathways',
              'Crisis intervention services'
            ].map((item, i) => (
              <motion.div
                key={i}
                className="support-item"
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { delay: i * 0.1, duration: 0.6 }
                  }
                }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ x: 10 }}
              >
                <span className="checkmark">→</span>
                <span>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Personalized Care Section with Image 2 */}
      <motion.section
        className="section-split reverse"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="split-grid">
            <div className="split-image">
              {/* IMAGE PLACEMENT 2: Care Planning Image */}
              <img src="/images/acr19.jpg" alt="Care Planning" />
            </div>

            <motion.div
              className="split-content"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 }
                }
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Personalized Care Planning</h2>
              <p className="section-text">
                Each resident receives individualized care plans and key workers assigned to support their emotional and behavioral needs, while promoting independence and self-fulfilling prophecy.
              </p>

              <div className="care-points">
                {[
                  'Custom care assessments',
                  'Dedicated key workers',
                  'Regular progress reviews',
                  'Flexible support options'
                ].map((point, i) => (
                  <motion.div
                    key={i}
                    className="care-point"
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0 }
                    }}
                    whileHover={{ x: 10 }}
                  >
                    <span>✓</span> {point}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Services;
